import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Play, Pause, RefreshCw, Settings, Loader2, Clock, 
  Zap, Bell, Mail, AlertTriangle, CheckCircle, XCircle,
  Activity, History, TestTube, ChevronRight, Eye
} from "lucide-react";

export default function AlertasExecutorPage() {
  const [status, setStatus] = useState(null);
  const [historial, setHistorial] = useState([]);
  const [notificaciones, setNotificaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testResults, setTestResults] = useState(null);
  const [runResults, setRunResults] = useState(null);
  const [intervalMinutes, setIntervalMinutes] = useState(15);
  const [reglas, setReglas] = useState([]);
  const [selectedRegla, setSelectedRegla] = useState("");
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [statusRes, historialRes, notifsRes, reglasRes] = await Promise.all([
        axios.get(`${API}/alertas-executor/status`, getAuthHeaders()),
        axios.get(`${API}/alertas-executor/historial?limit=50`, getAuthHeaders()),
        axios.get(`${API}/alertas-executor/notificaciones?limit=20`, getAuthHeaders()),
        axios.get(`${API}/reglas-alertas`, getAuthHeaders())
      ]);
      
      setStatus(statusRes.data);
      setIntervalMinutes(statusRes.data.interval_minutes || 15);
      setHistorial(historialRes.data.items || []);
      setNotificaciones(notifsRes.data.items || []);
      setReglas(reglasRes.data.items || []);
    } catch (err) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh status every 30 seconds when running
  useEffect(() => {
    if (status?.running) {
      const interval = setInterval(() => {
        axios.get(`${API}/alertas-executor/status`, getAuthHeaders())
          .then(res => setStatus(res.data))
          .catch(() => {});
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [status?.running, getAuthHeaders]);

  const handleStart = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/alertas-executor/start`, {}, getAuthHeaders());
      toast.success("Ejecutor iniciado");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al iniciar");
    } finally {
      setActionLoading(false);
    }
  };

  const handleStop = async () => {
    setActionLoading(true);
    try {
      await axios.post(`${API}/alertas-executor/stop`, {}, getAuthHeaders());
      toast.success("Ejecutor detenido");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al detener");
    } finally {
      setActionLoading(false);
    }
  };

  const handleRunNow = async () => {
    setActionLoading(true);
    setRunResults(null);
    try {
      const res = await axios.post(`${API}/alertas-executor/run-now`, {}, getAuthHeaders());
      setRunResults(res.data);
      toast.success(`Ejecución completada: ${res.data.reglas_evaluadas} reglas evaluadas`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al ejecutar");
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateConfig = async () => {
    try {
      await axios.put(`${API}/alertas-executor/config`, { interval_minutes: intervalMinutes }, getAuthHeaders());
      toast.success("Configuración actualizada");
      setShowConfigDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const handleTestRule = async () => {
    if (!selectedRegla) {
      toast.error("Selecciona una regla para probar");
      return;
    }
    setActionLoading(true);
    setTestResults(null);
    try {
      const res = await axios.post(`${API}/alertas-executor/test/${selectedRegla}`, {}, getAuthHeaders());
      setTestResults(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al probar regla");
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkRead = async (notifId) => {
    try {
      await axios.put(`${API}/alertas-executor/notificaciones/${notifId}/leer`, {}, getAuthHeaders());
      setNotificaciones(prev => prev.filter(n => n.id !== notifId));
    } catch (err) {
      toast.error("Error al marcar como leída");
    }
  };

  const formatDate = (isoString) => {
    if (!isoString) return "N/A";
    return new Date(isoString).toLocaleString("es-DO", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
    });
  };

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="flex-1 ml-0 md:ml-64 p-4 md:p-8 pt-14 md:pt-8 overflow-x-hidden">
        <div className="max-w-6xl">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
                <Activity className="w-7 h-7 text-blue-500" />
                Ejecutor de Alertas
              </h1>
              <p className="text-slate-500 mt-1">Sistema automático de evaluación y envío de alertas</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={fetchData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Actualizar
              </Button>
              {isAdmin && (
                <>
                  <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
                    <Settings className="w-4 h-4 mr-2" />
                    Configurar
                  </Button>
                  <Button variant="outline" onClick={() => setShowTestDialog(true)}>
                    <TestTube className="w-4 h-4 mr-2" />
                    Probar Regla
                  </Button>
                </>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center p-12"><Loader2 className="w-8 h-8 animate-spin" /></div>
          ) : (
            <>
              {/* Status Card */}
              <Card className="mb-6">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center ${status?.running ? 'bg-green-100' : 'bg-slate-100'}`}>
                        {status?.running ? (
                          <Activity className="w-8 h-8 text-green-600 animate-pulse" />
                        ) : (
                          <Pause className="w-8 h-8 text-slate-400" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="text-xl font-semibold">Estado del Ejecutor</h3>
                          <Badge variant={status?.running ? "default" : "secondary"} className={status?.running ? "bg-green-500" : ""}>
                            {status?.running ? "En Ejecución" : "Detenido"}
                          </Badge>
                        </div>
                        <p className="text-slate-500">
                          Intervalo: cada {status?.interval_minutes || 15} minutos
                        </p>
                        {status?.last_run && (
                          <p className="text-sm text-slate-400">
                            Última ejecución: {formatDate(status.last_run)}
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {isAdmin && (
                      <div className="flex gap-2">
                        {status?.running ? (
                          <Button onClick={handleStop} disabled={actionLoading} variant="outline" className="border-red-200 text-red-600 hover:bg-red-50">
                            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Pause className="w-4 h-4 mr-2" />}
                            Detener
                          </Button>
                        ) : (
                          <Button onClick={handleStart} disabled={actionLoading} className="bg-green-600 hover:bg-green-700 text-white">
                            {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                            Iniciar
                          </Button>
                        )}
                        <Button onClick={handleRunNow} disabled={actionLoading} variant="outline">
                          {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                          Ejecutar Ahora
                        </Button>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Stats Cards */}
              <div className="grid grid-cols-4 gap-4 mb-6">
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-blue-600">{status?.stats?.total_runs || 0}</div>
                    <p className="text-sm text-slate-500">Ejecuciones Totales</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-amber-600">{status?.stats?.alerts_triggered || 0}</div>
                    <p className="text-sm text-slate-500">Alertas Generadas</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-green-600">{status?.stats?.emails_sent || 0}</div>
                    <p className="text-sm text-slate-500">Emails Enviados</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4 text-center">
                    <div className="text-3xl font-bold text-red-600">{status?.stats?.errors || 0}</div>
                    <p className="text-sm text-slate-500">Errores</p>
                  </CardContent>
                </Card>
              </div>

              {/* Run Results (if available) */}
              {runResults && (
                <Card className="mb-6 border-blue-200 bg-blue-50/50">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Zap className="w-5 h-5 text-blue-600" />
                      Resultados de Última Ejecución Manual
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <div className="text-center p-3 bg-white rounded-lg">
                        <div className="text-2xl font-bold">{runResults.reglas_evaluadas}</div>
                        <p className="text-sm text-slate-500">Reglas Evaluadas</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <div className="text-2xl font-bold text-amber-600">
                          {runResults.resultados?.reduce((acc, r) => acc + (r.alertas_generadas || 0), 0)}
                        </div>
                        <p className="text-sm text-slate-500">Alertas Generadas</p>
                      </div>
                      <div className="text-center p-3 bg-white rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                          {runResults.resultados?.reduce((acc, r) => acc + (r.emails_enviados || 0), 0)}
                        </div>
                        <p className="text-sm text-slate-500">Emails Enviados</p>
                      </div>
                    </div>
                    <ScrollArea className="h-40">
                      <div className="space-y-2">
                        {runResults.resultados?.map((r, i) => (
                          <div key={i} className="flex items-center justify-between p-2 bg-white rounded text-sm">
                            <div className="flex items-center gap-2">
                              {r.error ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                              ) : r.alertas_generadas > 0 ? (
                                <AlertTriangle className="w-4 h-4 text-amber-500" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              )}
                              <span className="font-medium">{r.regla_nombre}</span>
                              <Badge variant="outline" className="text-xs">{r.modulo}</Badge>
                            </div>
                            <div className="flex items-center gap-3 text-slate-500">
                              {r.alertas_generadas > 0 && <span>{r.alertas_generadas} alertas</span>}
                              {r.emails_enviados > 0 && <span className="text-green-600">{r.emails_enviados} emails</span>}
                              {r.error && <span className="text-red-500 text-xs">{r.error}</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                  </CardContent>
                </Card>
              )}

              {/* Tabs: History & Notifications */}
              <Tabs defaultValue="historial" className="w-full">
                <TabsList className="mb-4">
                  <TabsTrigger value="historial" className="flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historial de Ejecuciones
                  </TabsTrigger>
                  <TabsTrigger value="notificaciones" className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Notificaciones ({notificaciones.length})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="historial">
                  <Card>
                    <CardContent className="p-0">
                      {historial.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          <History className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p>No hay historial de ejecuciones</p>
                        </div>
                      ) : (
                        <ScrollArea className="h-96">
                          <div className="divide-y">
                            {historial.map((h, i) => (
                              <div key={i} className="p-4 hover:bg-slate-50">
                                <div className="flex items-start justify-between">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="font-medium">{h.regla_nombre}</span>
                                      <Badge variant="outline">{h.modulo}</Badge>
                                      <Badge variant="secondary" className="text-xs">{h.evento}</Badge>
                                    </div>
                                    <p className="text-sm text-slate-600">{h.descripcion}</p>
                                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                                      <Clock className="w-3 h-3" />
                                      {formatDate(h.ejecutado_at)}
                                      {h.acciones_ejecutadas?.length > 0 && (
                                        <span className="flex items-center gap-1">
                                          <ChevronRight className="w-3 h-3" />
                                          {h.acciones_ejecutadas.map(a => (
                                            <Badge key={a} variant="outline" className="text-xs">
                                              {a === "email" && <Mail className="w-3 h-3 mr-1" />}
                                              {a === "notificacion" && <Bell className="w-3 h-3 mr-1" />}
                                              {a}
                                            </Badge>
                                          ))}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="notificaciones">
                  <Card>
                    <CardContent className="p-0">
                      {notificaciones.length === 0 ? (
                        <div className="p-8 text-center text-slate-500">
                          <Bell className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                          <p>No hay notificaciones pendientes</p>
                        </div>
                      ) : (
                        <ScrollArea className="h-96">
                          <div className="divide-y">
                            {notificaciones.map((n, i) => (
                              <div key={i} className="p-4 hover:bg-slate-50 flex items-start justify-between">
                                <div>
                                  <div className="flex items-center gap-2 mb-1">
                                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                                    <span className="font-medium">{n.regla_nombre}</span>
                                    <Badge variant="outline">{n.modulo}</Badge>
                                  </div>
                                  <p className="text-sm text-slate-600">{n.mensaje}</p>
                                  <p className="text-xs text-slate-400 mt-1">{formatDate(n.created_at)}</p>
                                </div>
                                <Button variant="ghost" size="sm" onClick={() => handleMarkRead(n.id)}>
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </div>

        {/* Config Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Configurar Ejecutor</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label>Intervalo de ejecución (minutos)</Label>
              <Input
                type="number"
                min={1}
                max={1440}
                value={intervalMinutes}
                onChange={e => setIntervalMinutes(parseInt(e.target.value) || 15)}
                className="mt-2"
              />
              <p className="text-sm text-slate-500 mt-2">
                El ejecutor evaluará todas las reglas activas cada {intervalMinutes} minutos.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>Cancelar</Button>
              <Button onClick={handleUpdateConfig} className="bg-[#0C314C] text-white">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Rule Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Probar Regla de Alerta</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label>Seleccionar Regla</Label>
              <select
                className="w-full mt-2 p-2 border rounded-md"
                value={selectedRegla}
                onChange={e => { setSelectedRegla(e.target.value); setTestResults(null); }}
              >
                <option value="">-- Seleccionar regla --</option>
                {reglas.map(r => (
                  <option key={r.id} value={r.id}>{r.nombre} ({r.modulo})</option>
                ))}
              </select>
              
              <Button 
                onClick={handleTestRule} 
                disabled={!selectedRegla || actionLoading} 
                className="mt-4 bg-[#0C314C] text-white"
              >
                {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TestTube className="w-4 h-4 mr-2" />}
                Probar sin enviar notificaciones
              </Button>

              {testResults && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold mb-2 flex items-center gap-2">
                    {testResults.alertas_encontradas > 0 ? (
                      <AlertTriangle className="w-5 h-5 text-amber-500" />
                    ) : (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    )}
                    Resultados de la Prueba
                  </h4>
                  <div className="grid grid-cols-2 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-slate-500">Regla:</span> {testResults.regla}
                    </div>
                    <div>
                      <span className="text-slate-500">Módulo:</span> {testResults.modulo}
                    </div>
                    <div>
                      <span className="text-slate-500">Evento:</span> {testResults.evento}
                    </div>
                    <div>
                      <span className="text-slate-500">Dentro de horario:</span>{" "}
                      {testResults.dentro_de_horario ? (
                        <Badge className="bg-green-100 text-green-700">Sí</Badge>
                      ) : (
                        <Badge className="bg-red-100 text-red-700">No</Badge>
                      )}
                    </div>
                  </div>
                  <div className="font-semibold text-lg mb-2">
                    Alertas encontradas: {testResults.alertas_encontradas}
                  </div>
                  {testResults.resultados?.length > 0 && (
                    <ScrollArea className="h-40 border rounded p-2">
                      {testResults.resultados.map((r, i) => (
                        <div key={i} className="p-2 border-b last:border-b-0 text-sm">
                          <div className="font-medium">{r.tipo}</div>
                          <div className="text-slate-600">{r.descripcion}</div>
                        </div>
                      ))}
                    </ScrollArea>
                  )}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowTestDialog(false); setTestResults(null); }}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
